import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Building2, Plus, TrendingUp, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import { toast } from 'sonner';

const API_URL = API_BASE;

export const Customers = () => {const { t } = useTranslation("ezparcel");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    type: '',
    sla: '',
    address: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('ezerpToken');
        const response = await fetch(`${API_URL}/api/ezparcel/customers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCustomers(data.map((c) => ({
            id: c.customer_id || c.id,
            name: c.name,
            email: c.email,
            phone: c.phone,
            type: c.type || 'retail',
            totalShipments: c.total_shipments || 0,
            avgMonthly: c.avg_monthly || 0,
            sla: c.sla || 'standard'
          })));
        }
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email || !newCustomer.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ezparcel/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
        },
        body: JSON.stringify({
          name: newCustomer.name,
          email: newCustomer.email,
          phone: newCustomer.phone,
          type: newCustomer.type || 'Standard',
          sla: newCustomer.sla || 'Standard',
          address: newCustomer.address
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add customer');
      }

      const savedCustomer = await response.json();

      // Convert API response to frontend format
      const customer = {
        id: savedCustomer.id,
        name: savedCustomer.name,
        email: savedCustomer.email,
        phone: savedCustomer.phone,
        type: savedCustomer.type,
        sla: savedCustomer.sla,
        totalShipments: savedCustomer.total_shipments,
        activeShipments: savedCustomer.active_shipments,
        monthlyVolume: savedCustomer.monthly_volume,
        onTimeRate: savedCustomer.on_time_rate,
        accountManager: savedCustomer.account_manager
      };

      setCustomers([customer, ...customers]);
      setNewCustomer({ name: '', email: '', phone: '', type: '', sla: '', address: '' });
      setIsAddDialogOpen(false);
      toast.success(`Customer "${newCustomer.name}" added successfully!`);
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error('Failed to add customer. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("customerAccounts", "Customer Accounts")}</h1>
          <p className="text-muted-foreground mt-2">{t("manageCustomerRelationships", "Manage customer relationships, SLAs, and shipping volumes")}

          </p>
        </div>
        <Button
          className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="add-customer-btn">
          
          <Plus className="mr-2 h-4 w-4" />{t("addCustomer", "Add Customer")}

        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {customers.map((customer) =>
        <Card key={customer.id} className="card-elevated">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[hsl(var(--accent)/.1)]">
                    <Building2 className="h-6 w-6 text-[hsl(var(--accent))]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{customer.name}</h3>
                    <p className="text-sm text-muted-foreground">{customer.id}</p>
                  </div>
                </div>
                <Badge
                variant="outline"
                className={customer.type === 'Enterprise' ? 'border-purple-500 text-purple-700' : ''}>
                
                  {customer.type}
                </Badge>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{t("email", "Email")}</p>
                    <p className="text-sm mt-1">{customer.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("phone", "Phone")}</p>
                    <p className="text-sm mt-1">{customer.phone}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground">{t("totalShipments", "Total Shipments")}</p>
                    <p className="text-xl font-bold mt-1">{customer.totalShipments.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("active", "Active")}</p>
                    <p className="text-xl font-bold mt-1 text-[hsl(var(--accent))]">
                      {customer.activeShipments}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("monthly", "Monthly")}</p>
                    <p className="text-xl font-bold mt-1">{customer.monthlyVolume}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground">{t("ontimeRate", "On-Time Rate")}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-lg font-bold text-green-600">{customer.onTimeRate}%</p>
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("slaTier", "SLA Tier")}</p>
                    <Badge className="mt-1">{customer.sla}</Badge>
                  </div>
                </div>

                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground">{t("accountManager", "Account Manager")}</p>
                  <p className="text-sm font-medium mt-1">{customer.accountManager}</p>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1">{t("viewAccount", "View Account")}

                </Button>
                  <Button variant="outline" size="sm" className="flex-1">{t("createShipment", "Create Shipment")}

                </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Customer Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />{t("addNewCustomer", "Add New Customer")}

            </DialogTitle>
            <DialogDescription>{t("enterTheCustomers", "Enter the customer's information to create a new account.")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="customer-name">{t("companyName", "Company Name *")}</Label>
              <Input
                id="customer-name"
                placeholder="Acme Corporation"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} />
              
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="customer-email">{t("email", "Email *")}</Label>
                <Input
                  id="customer-email"
                  type="email"
                  placeholder="contact@acme.com"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} />
                
              </div>
              <div className="grid gap-2">
                <Label htmlFor="customer-phone">{t("phone", "Phone *")}</Label>
                <Input
                  id="customer-phone"
                  placeholder="+1 (555) 123-4567"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} />
                
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="customer-type">{t("customerType", "Customer Type")}</Label>
                <Select
                  value={newCustomer.type}
                  onValueChange={(value) => setNewCustomer({ ...newCustomer, type: value })}>
                  
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Standard">{t("standard", "Standard")}</SelectItem>
                    <SelectItem value="Premium">{t("premium", "Premium")}</SelectItem>
                    <SelectItem value="Enterprise">{t("enterprise", "Enterprise")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="customer-sla">{t("slaTier", "SLA Tier")}</Label>
                <Select
                  value={newCustomer.sla}
                  onValueChange={(value) => setNewCustomer({ ...newCustomer, sla: value })}>
                  
                  <SelectTrigger>
                    <SelectValue placeholder="Select SLA" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Standard">{t("standard", "Standard")}</SelectItem>
                    <SelectItem value="Premium">{t("premium", "Premium")}</SelectItem>
                    <SelectItem value="Priority">{t("priority", "Priority")}</SelectItem>
                    <SelectItem value="24/7 Support">{t("247Support", "24/7 Support")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="customer-address">{t("businessAddress", "Business Address")}</Label>
              <Textarea
                id="customer-address"
                placeholder="123 Business Ave, Suite 100&#10;City, State 12345"
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                rows={3} />
              
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleAddCustomer}
              disabled={isLoading}
              className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
              
              {isLoading ? 'Adding...' : 'Add Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};